import { Box, Container, Flex, Grid, Heading, Text } from '@chakra-ui/react';
import {
  AccordionItemContent,
  AccordionItemTrigger,
  AppLink,
  Badge,
  GradientShadowOverlay,
  Image,
  pxToRem,
} from '@cyberkongz/ui';

import { CARDS_COMMUNITY, CARDS_TOOLS } from './content/cards';
import {
  ActiveCardSidebar,
  CardItemWrapper,
  cardsSectionBackground,
  CardsSectionWrapper,
  IntroSectionWrapper,
  introTextStyles,
  ListOfPartnerships,
  useCardsSection,
} from './Shared';

function IntroSection() {
  return (
    <IntroSectionWrapper
      sxSection={{
        backgroundImage: 'url(/static/blueprint/community/intro_bg.jpg)',
      }}
    >
      <Heading lineHeight="base">
        COMMUNITY
        <br />
        CONTRIBUTIONS &
      </Heading>
      <Heading variant="displaySecondary">GOVERNANCE</Heading>
      <Text css={{ ...introTextStyles }}>
        CyberKongz is community-driven and that is one of the elements that makes our project so
        special. We host some of the finest developers, artists, and traders in the NFT and DeFi
        space, who consistently contribute their skills and time to execute the community`s ideas.
      </Text>
    </IntroSectionWrapper>
  );
}

function Authors({
  authors,
  label,
}: {
  authors: { href: string; label: string }[];
  label?: string;
}) {
  return (
    <Flex
      css={{
        color: 'text',
        textTransform: 'uppercase',
        fontWeight: 'medium',
        fontSize: ['sm'],
        flexDirection: ['column', 'row'],
      }}
    >
      <Text mr={2}>{label || 'created by'}</Text>
      <Flex>
        {authors.map(({ href, label }) => (
          <AppLink
            key={href}
            href={href}
            css={{
              borderRight: '1px solid white',
              px: 2,
              '&:last-of-type': { borderRight: 'none' },
              '&:first-of-type': { pl: 0 },
            }}
          >
            {label}
          </AppLink>
        ))}
      </Flex>
    </Flex>
  );
}

function CardsSection() {
  const { activeItem, handleChange } = useCardsSection();
  const activeCardSidebar = activeItem ? (
    <ActiveCardSidebar {...CARDS_COMMUNITY[parseInt(activeItem)]} basePath="community" />
  ) : null;

  return (
    <CardsSectionWrapper
      {...{
        activeItem,
        handleChange,
        activeCardSidebar,
        disableBackground: true,
      }}
    >
      {CARDS_COMMUNITY.map(({ title, description, image, authors, poweredBy, href }, i) => (
        <CardItemWrapper key={i} value={i}>
          <AccordionItemTrigger>
            <Flex css={{ justifyContent: 'space-between', alignItems: 'center', flex: 1 }}>
              <Badge badgeColor="primary" withDot>
                Launched
              </Badge>
              {image ? (
                <Box css={{ display: ['none', 'block'] }}>
                  <Image src={`/static/blueprint/community/logo_${image}.png`} alt={title} />
                </Box>
              ) : null}
            </Flex>
          </AccordionItemTrigger>
          {href ? (
            <AppLink href={href}>
              <Heading size="2xs" css={{ mt: [4, 12], color: 'text' }}>
                {title}
              </Heading>
            </AppLink>
          ) : (
            <Heading size="2xs" css={{ mt: [4, 12], color: 'text' }}>
              {title}
            </Heading>
          )}
          <Grid gap={1} mt={3}>
            {authors ? <Authors {...{ authors }} /> : null}
            {poweredBy ? <Authors {...{ authors: poweredBy, label: 'Powered by' }} /> : null}
          </Grid>
          <AccordionItemContent>
            <Box color="textSecondary">{description}</Box>
            <Box display={['block', 'none']}>{activeCardSidebar}</Box>
          </AccordionItemContent>
        </CardItemWrapper>
      ))}
    </CardsSectionWrapper>
  );
}

function CardsSectionTools() {
  const { activeItem, handleChange } = useCardsSection();
  const activeCardSidebar = activeItem ? (
    <ActiveCardSidebar {...CARDS_TOOLS[parseInt(activeItem)]} basePath="community" />
  ) : null;

  return (
    <>
      <Box pt={pxToRem(128)} mb={6}>
        <Container>
          <Heading size="sm">Community-led tools</Heading>
        </Container>
      </Box>
      <CardsSectionWrapper
        {...{
          activeItem,
          handleChange,
          activeCardSidebar,
          disableBackground: true,
        }}
      >
        {CARDS_TOOLS.map(({ title, description, href, authors }, i) => (
          <CardItemWrapper key={title} value={i}>
            <AccordionItemTrigger>
              <Flex css={{ justifyContent: 'space-between', alignItems: 'center', flex: 1 }}>
                <Badge badgeColor="primary" withDot>
                  Launched
                </Badge>
              </Flex>
            </AccordionItemTrigger>
            <AppLink href={href}>
              <Heading size="2xs" css={{ mt: [4, 12], color: 'text' }}>
                {title}
              </Heading>
            </AppLink>
            <Grid gap={1} mt={3}>
              {authors ? <Authors {...{ authors }} /> : null}
            </Grid>
            <AccordionItemContent px={0} pb={0} pt={5}>
              <Box color="textSecondary">{description}</Box>
              <Box display={['block', 'none']}>{activeCardSidebar}</Box>
            </AccordionItemContent>
          </CardItemWrapper>
        ))}
      </CardsSectionWrapper>
    </>
  );
}

export function CommunityTab() {
  return (
    <>
      <IntroSection />
      <Grid position="relative">
        <Box
          css={{
            backgroundImage: 'url(/static/blueprint/community/cards_bg.jpg)',
            ...cardsSectionBackground,
          }}
        />
        <Box gridColumn={1} gridRow={1}>
          <CardsSection />
          <CardsSectionTools />
          <GradientShadowOverlay height={pxToRem(500)} />
        </Box>
      </Grid>
      <ListOfPartnerships />
    </>
  );
}
